import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTaktBoard } from "./useTaktBoard";
import { useActivityLookups } from "./useActivityLookups";
import TaktGrid from "../TaktGrid/TaktGrid";
import type { BarClickInfo, SectionGraphInfo } from "../TaktGrid/TaktGrid";
import EditEntryModal from "../EditEntryModal/EditEntryModal";
import CascadeWorkbenchModal from "../../shared/cascadeWorkbench/CascadeWorkbenchModal/CascadeWorkbenchModal";
import CascadeBanner from "../CascadeBanner/CascadeBanner";
import ExportTaktModal from "../ExportTaktModal/ExportTaktModal";
import TaktEditorModal from "../TaktEditorModal/TaktEditorModal";
import TaktToolbar from "../TaktToolbar/TaktToolbar";
import TaktInspector from "../TaktInspector/TaktInspector";
import ContextualLegend from "../ContextualLegend/ContextualLegend";
import AppliedFilterBar from "../AppliedFilterBar/AppliedFilterBar";
import FilterRail from "../FilterRail/FilterRail";
import type { InspectorInfo } from "../types";
import DependencyGraphModal from "../../dependency-graph/DependencyGraphModal/DependencyGraphModal";
import { useDependencyGraph } from "../../dependency-graph/useDependencyGraph";
import TaktSectionDepEditorHost from "../TaktSectionDepEditorHost/TaktSectionDepEditorHost";
import { fetchTaktSectionDependencyGraph } from "../../../api/clientV2";
import type {
  ActivitySearchResult,
  WorkbenchResult,
} from "../../../models";
import "./TaktBoardPage.css";

/* Trade legend bar is disabled for now (trade reads off the bars + the inspector
 * Trade column). Flip to re-enable; the component + wiring are kept. */
const TRADE_LEGEND_ENABLED = false;

interface TaktBoardPageProps {
  onGoToPullPlanByTaktSection?: (sectionName: string) => void;
}

const TaktBoardPage: React.FC<TaktBoardPageProps> = ({
  onGoToPullPlanByTaktSection,
}) => {
  const {
    loading,
    error,
    data,
    colorBy,
    setColorBy,
    filters,
    matchMode,
    setMatchMode,
    toggleAttrValue,
    setCompare,
    clearAttrFilter,
    clearAllFilters,
    attributes,
    isFiltering,
    activeFilterCount,
    totalActivities,
    shownActivities,
    shownSections,
    activeColors,
    selectedScheduleId,
    monthSpans,
    todayIdx,
    flatRows,
    colorStyleSheet,
    scrollerRef,
    virtuosoRef,
    scrollToSection,
    handleVisibleRange,
    reload,
    updateBulkEntryDates,
    pxPerDay,
    defaultPx,
    zoomReadout,
    onZoom,
    zoomConfig,
  } = useTaktBoard();

  /* ---- Takt Editor modal — null = closed, else the tab it opens on.
     "Manage trades" entries open straight onto the Work Codes tab, where the
     trade buckets now live. ---- */
  const [taktEditorTab, setTaktEditorTab] = useState<"structure" | "codes" | null>(null);

  /* ---- Bottom inspector (hover preview) ---- */
  const [inspector, setInspector] = useState<InspectorInfo | null>(null);

  // The inspector is a snapshot of the last-hovered bar. A board refresh (e.g.
  // after a Takt Editor save changes a bar's trade) re-renders the bars but
  // can't update an already-captured snapshot — so drop it on any data change
  // and let the next hover re-read fresh values.
  useEffect(() => {
    setInspector(null);
  }, [data]);

  /* ---- Filters rail open/closed ---- */
  const [railOpen, setRailOpen] = useState(false);

  /* ---- Edit modal state ---- */
  const [editInfo, setEditInfo] = useState<BarClickInfo | null>(null);

  /* ---- Cascade state ---- */
  const [cascadeResult, setCascadeResult] = useState<WorkbenchResult | null>(null);
  const [cascadedActivityIds, setCascadedActivityIds] = useState<Set<number>>(new Set());
  const [sourceActivityId, setSourceActivityId] = useState<number | null>(null);
  const [bannerInfo, setBannerInfo] = useState<{
    workCode: string;
    label: string;
    finishDelta: number;
    totalCascaded: number;
    result: WorkbenchResult;
  } | null>(null);

  /* ---- Export modal state ---- */
  const [showExportModal, setShowExportModal] = useState(false);

  /* ---- Dependency graph modal state (per-section) ---- */
  const [graphSection, setGraphSection] = useState<SectionGraphInfo | null>(null);
  /* ---- Dep editor host state (per-section) ---- */
  const [depEditorSection, setDepEditorSection] = useState<SectionGraphInfo | null>(null);
  const graphFetcher = useMemo(
    () =>
      graphSection && selectedScheduleId
        ? () => fetchTaktSectionDependencyGraph(selectedScheduleId, graphSection.name)
        : null,
    [graphSection, selectedScheduleId],
  );
  const graph = useDependencyGraph(graphFetcher);

  const handleSectionPullPlanClick = useCallback(
    (section: SectionGraphInfo) => {
      if (!selectedScheduleId || !onGoToPullPlanByTaktSection) return;
      // The backend filters activities by Takt_SectionName + same-leaf
      // untagged, so nested sub-leaves like "Corridor Finishes" inside a
      // unit-finishes WBS aren't dragged in.
      onGoToPullPlanByTaktSection(section.name);
    },
    [selectedScheduleId, onGoToPullPlanByTaktSection],
  );

  /* ---- Activity ID → work code / section / zone lookups ---- */
  const { workCodeMap: activityWorkCodeMap, sectionMap, zoneMap } = useActivityLookups(data);

  /* ---- Clear cascade highlights ---- */
  const clearCascade = useCallback(() => {
    setBannerInfo(null);
    setCascadedActivityIds(new Set());
    setSourceActivityId(null);
  }, []);

  /* ---- Bar click → open edit modal ---- */
  const handleBarClick = useCallback((info: BarClickInfo) => {
    if (bannerInfo) clearCascade();
    setEditInfo(info);
  }, [bannerInfo, clearCascade]);

  /* ---- Search pick → open the same edit modal as a bar click ---- */
  const handleSearchSelect = useCallback((r: ActivitySearchResult) => {
    if (bannerInfo) clearCascade();
    setEditInfo({
      workCode: r.work_code || r.task_code,
      label: r.prefix || r.task_name,
      activityIds: [r.id],
      start: "",
      finish: "",
      status: r.status,
      sectionName: r.section_name || "",
      taktB: r.takt_b || "",
      taktC: r.takt_c || "",
      isCritical: false,
    });
  }, [bannerInfo, clearCascade]);

  const handleEditClose = useCallback(() => {
    setEditInfo(null);
  }, []);

  /* ---- Unified edit modal: applied successfully ---- */
  const handleEditApplied = useCallback((result: WorkbenchResult) => {
    if (!editInfo) return;
    const source = result.source;
    if (!source) {
      setEditInfo(null);
      return;
    }
    // Bulk optimistic update: source + every moved row. Under "Apply Edits
    // Only" the cascade was simulated but NOT persisted, so apply just the
    // source — the successors keep their stored dates (the F9 badge flags it).
    const movers = result.ignored_cascade
      ? []
      : result.rows.filter((r) => r.moved);
    const updates = [
      {
        activityIds: editInfo.activityIds,
        newStart: source.new_start ?? "",
        newFinish: source.new_end ?? "",
      },
      ...movers.map((r) => ({
        activityIds: [r.activity_id],
        newStart: r.new_start ?? "",
        newFinish: r.new_end ?? "",
      })),
    ];
    updateBulkEntryDates(updates);
    setCascadedActivityIds(new Set(movers.map((r) => r.activity_id)));
    setSourceActivityId(source.activity_id);
    const srcWc = activityWorkCodeMap.get(source.activity_id) || "?";
    setBannerInfo({
      workCode: srcWc,
      label: source.task_name,
      finishDelta: source.finish_delta,
      totalCascaded: result.ignored_cascade ? 0 : result.summary.impacted,
      result,
    });
    setEditInfo(null);
  }, [editInfo, activityWorkCodeMap, updateBulkEntryDates]);

  /* ---- Cascade preview (banner re-open) is read-only ---- */
  const handleCascadeCancel = useCallback(() => {
    setCascadeResult(null);
  }, []);

  /* ---- Banner: show list ---- */
  const handleShowCascadeList = useCallback(() => {
    if (bannerInfo) {
      setCascadeResult(bannerInfo.result);
    }
  }, [bannerInfo]);

  /* ---- Render ---- */

  if (error) {
    return <div className="takt-page-error">{error}</div>;
  }

  if (loading || !data) {
    return <div className="takt-page-loading">Loading Takt board...</div>;
  }

  if (data.sections.length === 0) {
    return <div className="takt-page-empty">No Takt data found for this schedule.</div>;
  }

  const isFilteredEmpty = isFiltering && shownSections === 0;
  const criticalFilterActive = (filters.critical?.length ?? 0) > 0;

  return (
    <div className="takt-page">
      <TaktToolbar
        scheduleId={selectedScheduleId}
        activeColors={activeColors}
        onSearchSelect={handleSearchSelect}
        filtersOpen={railOpen}
        onToggleFilters={() => setRailOpen((o) => !o)}
        activeFilterCount={activeFilterCount}
        pxPerDay={pxPerDay}
        defaultPx={defaultPx}
        zoomReadout={zoomReadout}
        onZoom={onZoom}
        onExport={() => setShowExportModal(true)}
        onOpenTaktEditor={() => setTaktEditorTab("structure")}
      />

      {/* Trade legend disabled — the trade now reads off each bar + the inspector's
          Trade column. Kept wired so it's a one-flag revert. */}
      {TRADE_LEGEND_ENABLED && (
        <ContextualLegend
          trades={data.trades || []}
          colorBy={colorBy}
          onOpenManageTrades={() => setTaktEditorTab("codes")}
          selectedTradeIds={new Set(filters.trade || [])}
          onToggleTrade={(id) => toggleAttrValue("trade", id)}
        />
      )}

      <AppliedFilterBar
        attributes={attributes}
        filters={filters}
        matchMode={matchMode}
        shownActivities={shownActivities}
        totalActivities={totalActivities}
        shownSections={shownSections}
        onClearAttr={clearAttrFilter}
        onClearAll={clearAllFilters}
      />

      {bannerInfo && (
        <CascadeBanner
          sourceWorkCode={bannerInfo.workCode}
          sourceLabel={bannerInfo.label}
          finishDelta={bannerInfo.finishDelta}
          totalCascaded={bannerInfo.totalCascaded}
          criticalPathImpact={bannerInfo.result.summary.critical_path_impact}
          onShowList={handleShowCascadeList}
          onClear={clearCascade}
        />
      )}

      <div className="takt-body">
        {railOpen && (
          <FilterRail
            data={data}
            attributes={attributes}
            filters={filters}
            matchMode={matchMode}
            shownActivities={shownActivities}
            totalActivities={totalActivities}
            shownSections={shownSections}
            colorBy={colorBy}
            onSetColorBy={setColorBy}
            onOpenManageTrades={() => setTaktEditorTab("codes")}
            onToggleValue={toggleAttrValue}
            onSetCompare={setCompare}
            onClearAttr={clearAttrFilter}
            onClearAll={clearAllFilters}
            onSetMatchMode={setMatchMode}
            onNavigateValue={(key, vid) => {
              if (key === "section") scrollToSection(vid);
            }}
            onClose={() => setRailOpen(false)}
          />
        )}
        {isFilteredEmpty ? (
        <div className="takt-empty-filter">
          <div>No sections match these filters.</div>
          <button onClick={clearAllFilters}>Clear filters</button>
        </div>
      ) : (
        <TaktGrid
          workdays={data.workdays}
          colors={activeColors}
          monthSpans={monthSpans}
          todayIdx={todayIdx}
          flatRows={flatRows}
          colorStyleSheet={colorStyleSheet}
          scrollerRef={scrollerRef}
          virtuosoRef={virtuosoRef}
          onBarClick={handleBarClick}
          onBarHover={setInspector}
          onSectionGraphClick={setGraphSection}
          onSectionPullPlanClick={
            onGoToPullPlanByTaktSection ? handleSectionPullPlanClick : undefined
          }
          onSectionDepEditorClick={setDepEditorSection}
          cascadedActivityIds={cascadedActivityIds}
          sourceActivityId={sourceActivityId}
          colorBy={colorBy}
          criticalFilterActive={criticalFilterActive}
          zoomConfig={zoomConfig}
          pxPerDay={pxPerDay}
          onZoom={onZoom}
          onVisibleRangeChange={handleVisibleRange}
        />
      )}
      </div>

      <TaktInspector info={inspector} />

      {editInfo && selectedScheduleId && (
        <EditEntryModal
          scheduleId={selectedScheduleId}
          activityId={editInfo.activityIds[0]}
          workCode={editInfo.workCode}
          label={editInfo.label}
          sectionName={editInfo.sectionName}
          taktB={editInfo.taktB}
          taktC={editInfo.taktC}
          isCritical={editInfo.isCritical}
          workdays={data.workdays}
          colors={activeColors}
          activityWorkCodeMap={activityWorkCodeMap}
          sectionMap={sectionMap}
          zoneMap={zoneMap}
          onClose={handleEditClose}
          onApplied={handleEditApplied}
        />
      )}

      {cascadeResult && data && selectedScheduleId && (
        <CascadeWorkbenchModal
          scheduleId={selectedScheduleId}
          readOnly
          presetResult={cascadeResult}
          colors={activeColors}
          activityWorkCodeMap={activityWorkCodeMap}
          sectionMap={sectionMap}
          zoneMap={zoneMap}
          onCancel={handleCascadeCancel}
        />
      )}

      {showExportModal && selectedScheduleId && (
        <ExportTaktModal
          scheduleId={selectedScheduleId}
          trades={data?.trades || []}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {taktEditorTab !== null && selectedScheduleId && (
        <TaktEditorModal
          scheduleId={selectedScheduleId}
          initialTab={taktEditorTab}
          onClose={() => setTaktEditorTab(null)}
          onSaved={() => {
            setTaktEditorTab(null);
            reload();
          }}
        />
      )}

      {graphSection && (
        <DependencyGraphModal
          data={graph.data}
          loading={graph.loading}
          error={graph.error}
          onClose={() => setGraphSection(null)}
        />
      )}

      {depEditorSection && selectedScheduleId && (
        <TaktSectionDepEditorHost
          scheduleId={selectedScheduleId}
          sectionName={depEditorSection.name}
          sectionTitle={depEditorSection.name}
          colors={activeColors}
          activityWorkCodeMap={activityWorkCodeMap}
          sectionMap={sectionMap}
          zoneMap={zoneMap}
          onSaved={() => {
            setDepEditorSection(null);
            reload();
          }}
          onClose={() => setDepEditorSection(null)}
        />
      )}
    </div>
  );
};

export default TaktBoardPage;
